import { useEffect, useLayoutEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { authorize, clearUser, setUser, checkLicensePlateHash } from "../redux/ducks/user";
import { ReducersTypes } from "../redux/reducers/Models";
import IndexView from "./view";
import { setCustomer } from "@/redux/ducks/customer";
import useQuery from "@/hooks/useQuery";
import useCaptchaEnterprise from "@/hooks/useCaptchaEnterprise";
import useCaptcha from "@/hooks/useCaptcha";
import { useSingleFlag } from "@/hooks/useFlags";
import Loading from "@/atomic/atoms/Loading";

export default function Index() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const params = useQuery();
  const user = useSelector((state: ReducersTypes) => state.userReducer);
  const { isEnabled: isCaptchaMtcCaptcha, isLoading: isLoadingCaptchaFlag } = useSingleFlag('PD_CAPTCHA_MTCAPTCHA');

  const [isVisibleMenu, setIsVisibleMenu] = useState(false);
  const [isVisibleModal, setIsVisibleModal] = useState(false);
  const [isVisibleModalLogin, setIsVisibleModalLogin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAcceptedTerms, setIsAcceptedTerms] = useState(false);
  const [error, setError] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
  const lastProcessedPlate = useRef<string | null>(null);
  const lastProcessedHashFromUrl = useRef<string | null>(null);

  const captchaName = "mtcaptcha-intern";

  useCaptcha({
    setIsVerified: isCaptchaMtcCaptcha ? setIsVerified : () => {},
    captchaName
  });

  useCaptchaEnterprise({
    setIsVerified: !isCaptchaMtcCaptcha ? setIsCaptchaVerified : () => {},
    captchaName: "home-captcha",
    action: "login"
  });

  useEffect(() => {
    if (isCaptchaMtcCaptcha) {
      // @ts-expect-error
      const status = window?.mtcaptcha?.getStatus?.(captchaName);
      // @ts-expect-error
      const token = window?.mtcaptcha?.getVerifiedToken?.(captchaName);
      
      if (status?.isVerified && token) {
        setIsVerified(true);
      } else {
        setIsVerified(false);
      }
    }
  }, [location.pathname, isCaptchaMtcCaptcha, captchaName]);

  const toggleMenu = () => setIsVisibleMenu(isVisibleMenu => !isVisibleMenu);
  const toggleModal = () => setIsVisibleModal(isVisibleModal => !isVisibleModal);
  const toggleModalLogin = () => setIsVisibleModalLogin(isVisibleModalLogin => !isVisibleModalLogin);
  const toggleTerms = () => setIsAcceptedTerms(isAcceptedTerms => !isAcceptedTerms);

  const goToCreateAccount = () => navigate("/register");

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
  
    const cleanPlate = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    dispatch(setUser({ [name]: cleanPlate }));
  }

  const getIsValidForm = () => {
    const isCaptchaValid = isCaptchaMtcCaptcha ? isVerified : isCaptchaVerified;
    const cleanPlate = (user?.plate as string)?.replace(/[^A-Z0-9]/g, "") || "";
    return cleanPlate.length >= 5 && isAcceptedTerms && isCaptchaValid;
  };

  const resetCaptcha = () => {
    if (isCaptchaMtcCaptcha) {
      // @ts-expect-error
      window?.mtcaptcha?.resetUI(captchaName);
      setIsVerified(false);
    } else {
      if (typeof window !== "undefined" && (window as any).grecaptcha?.reset) {
        (window as any).grecaptcha.reset();
      }
      setIsCaptchaVerified(false);
    }
  };

  const handleAuthorize = (success: boolean, errorMessage?: string) => {
    setIsLoading(false);

    if (!success) {
      if (errorMessage === "timeout") setError(true);
      toggleModal();
      resetCaptcha();
      return;
    }

    navigate("/register/avulso");
  }

  const handleAuthorizeFromUrl = (success: boolean, errorMessage?: string) => {
    setIsLoading(false);

    if (!success) {
      if (errorMessage === "timeout") setError(true);
      toggleModal();
      resetCaptcha();
      return;
    }

    // Se a rota for do tipo siga-facil, não deve passar pela validação de PIN
    const hash = location.hash;
    const isSigaFacilRoute = hash && hash.includes('#siga-facil');

    // Sempre navega para a tela de dados pessoais do fluxo avulso
    // Exceto se for uma rota siga-facil
    if (!isSigaFacilRoute) {
      navigate("/register/avulso");
      return;
    }

    navigate("/home");
  }

  const searchPlateFromUrl = async (plateFromUrl: string) => {
    const cleanPlate = plateFromUrl.toUpperCase().replace(/[^A-Z0-9]/g, "");
    
    if (!cleanPlate || cleanPlate.length < 5) {
      return;
    }

    dispatch(setUser({ plate: cleanPlate }));
    setIsAcceptedTerms(true);
    setIsVerified(true);
    setIsCaptchaVerified(true);
    setIsLoading(true);

    try {
      dispatch(
        authorize({
          plate: cleanPlate,
          token: '',
          callback: handleAuthorizeFromUrl,
        })
      );
    } catch (error) {
      setIsLoading(false);
    }
  }

  const goToDebts = async (e: React.ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!getIsValidForm() || isLoading) return;

    setIsLoading(true);

    try {
      const cleanPlate = (user?.plate as string)?.replace(/[^A-Z0-9]/g, "") || "";
      
      if (isCaptchaMtcCaptcha) {
        // @ts-expect-error
        const mtcaptchaToken = window?.mtcaptcha?.getVerifiedToken?.(captchaName);
        // @ts-expect-error
        const mtcaptchaStatus = window?.mtcaptcha?.getStatus?.(captchaName);
        
        
        if (!mtcaptchaToken || !mtcaptchaStatus?.isVerified) {
          setError(true);
          toggleModal();
          setIsLoading(false);
          resetCaptcha();
          return;
        }

        dispatch(
          authorize({
            plate: cleanPlate,
            token: mtcaptchaToken,
            callback: handleAuthorize,
          })
        );
      } else {
        const recaptchaToken = (window as any)?.recaptchaToken;
        
        if (!recaptchaToken) {
          if ((window as any).executeRecaptcha) {
            (window as any).executeRecaptcha();
          }
          setError(true);
          toggleModal();
          setIsLoading(false);
          setIsCaptchaVerified(false);
          return;
        }

        dispatch(
          authorize({
            plate: cleanPlate,
            token: recaptchaToken,
            callback: handleAuthorize,
            recaptchaAction: !isCaptchaMtcCaptcha && (window as any).getRecaptchaAction ? (window as any).getRecaptchaAction() : undefined,
          })
        );
      }
    } catch (error) {
      setIsLoading(false);
    }
  }

  useLayoutEffect(() => {
    const hash = location.hash;
    
    let hasValidHash = false;
    if (hash && hash.includes('#siga-facil') && hash.includes('?')) {
      const queryString = hash.split('?')[1];
      const hashParams = new URLSearchParams(queryString);
      hasValidHash = !!hashParams.get("q");
    }
    
    if (!hasValidHash) {
      dispatch(setUser({ plate: '' }));
      dispatch(setCustomer({
        register: undefined,
        forgot: undefined,
        customer: undefined,
        products: undefined,
        client: undefined,
        vehicles: undefined,
        extract: undefined,
        recharges: undefined,
        isLoadingVehicles: false,
        tickets: undefined,
        debts: undefined,
      }));
      dispatch(clearUser());
    }
    
    if (params.get("auth")) {
      toggleModalLogin();
    }
  }, []);


  useEffect(() => {
    const hash = location.hash;
    if (hash && hash.includes('#siga-facil')) {
      let hashFromUrl: string | null = null;
      if (hash.includes('?')) {
        const queryString = hash.split('?')[1];
        const params = new URLSearchParams(queryString);
        hashFromUrl = params.get("q");
      }
      
      if (hashFromUrl && hashFromUrl !== lastProcessedHashFromUrl.current) {
        lastProcessedHashFromUrl.current = hashFromUrl;
        setIsLoading(true);
        
        dispatch(
          checkLicensePlateHash({
            hash: hashFromUrl,
            callback: (success: boolean, plate?: string) => {
              setIsLoading(false);
              if (success && plate) {
                setTimeout(() => {
                  searchPlateFromUrl(plate);
                }, 100);
              } else {
                setError(true);
                toggleModal();
              }
            },
          })
        );
      } else if (!hashFromUrl) {
        lastProcessedHashFromUrl.current = null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  return (
    <IndexView
        toggleMenu={toggleMenu}
        isVisibleMenu={isVisibleMenu}
        user={user}
        onChange={onChange}
        goToDebts={goToDebts}
        isDisabled={!getIsValidForm()}
        isLoading={isLoading}
        error={error}
        isVisibleModal={isVisibleModal}
        toggleModal={toggleModal}
        isAcceptedTerms={isAcceptedTerms}
        toggleTerms={toggleTerms}
        isVisibleModalLogin={isVisibleModalLogin}
        toggleModalLogin={toggleModalLogin}
        goToCreateAccount={goToCreateAccount}
        isCaptchaVerified={isCaptchaVerified}
        isCaptchaMtcCaptcha={isCaptchaMtcCaptcha}
        isLoadingCaptchaFlag={isLoadingCaptchaFlag}
      />
  );
}
